import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Play, FileText, List, Zap, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import {
  fetchRegulatoryRequirements,
  processRegulatoryDocument,
  convertRegulatoryDocMarkdownChunk,
  extractRegulatoryRequirements,
  generateRegulatoryEmbeddings,
  type RegulatoryDocument,
  type RegulatoryRequirement,
} from '../../services/api';

interface RegulatoryDocDetailProps {
  document: RegulatoryDocument;
  onBack: () => void;
  onRefresh: () => void;
}

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  standard: 'bg-blue-100 text-blue-700',
  informational: 'bg-gray-100 text-gray-600',
};

const STEP_STATUS = {
  uploaded: 0,
  processing: 0.5,  // OCR in progress, not yet complete
  ocr_complete: 1,
  converting_markdown: 1.5,
  markdown_ready: 2,
  requirements_extracted: 3,
  failed: -1,
} as Record<string, number>;

export const RegulatoryDocDetail: React.FC<RegulatoryDocDetailProps> = ({ document: doc, onBack, onRefresh }) => {
  const [requirements, setRequirements] = useState<RegulatoryRequirement[]>([]);
  const [isLoadingReqs, setIsLoadingReqs] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [expandedReq, setExpandedReq] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const currentStep = STEP_STATUS[doc.status] ?? 0;
  const [markdownConverting, setMarkdownConverting] = useState(false);
  const [mdProgress, setMdProgress] = useState<{ current: number; total: number } | null>(null);
  const [mdStreamChars, setMdStreamChars] = useState(0);
  const markdownProgressLabel = mdProgress
    ? `Converting section ${mdProgress.current + 1} of ${mdProgress.total}...${mdStreamChars > 0 ? ` (${mdStreamChars.toLocaleString()} chars)` : ''}`
    : 'Starting conversion...';
  const cancelRef = useRef(false);

  // Drive chunk-by-chunk markdown conversion
  const runMarkdownConversion = useCallback(async () => {
    setMarkdownConverting(true);
    setActionError(null);
    setMdStreamChars(0);
    cancelRef.current = false;
    try {
      let done = false;
      let streamRetries = 0;
      const MAX_STREAM_RETRIES = 3;
      while (!done && !cancelRef.current) {
        console.log('[Markdown] Processing next chunk...');
        setMdStreamChars(0);
        try {
          const result = await convertRegulatoryDocMarkdownChunk(doc.id, (chars) => {
            setMdStreamChars(chars);
          });
          console.log(`[Markdown] Chunk ${result.chunkIndex + 1}/${result.totalChunks} done=${result.done}`);
          setMdProgress({ current: result.chunkIndex, total: result.totalChunks });
          done = result.done;
          streamRetries = 0;
        } catch (err) {
          // Stream can disconnect if Cloudflare edge times out while Claude is generating.
          // The backend saves chunks to R2 via ctx.waitUntil(), so retrying is safe —
          // it will skip already-completed chunks.
          if (err instanceof Error && err.message.includes('Stream ended unexpectedly') && streamRetries < MAX_STREAM_RETRIES) {
            streamRetries++;
            console.warn(`[Markdown] Stream interrupted, retrying (${streamRetries}/${MAX_STREAM_RETRIES})...`);
            await new Promise(resolve => setTimeout(resolve, 3000));
            continue;
          }
          throw err;
        }
      }
      if (!cancelRef.current) {
        console.log('[Markdown] Conversion complete');
        onRefresh();
      }
    } catch (err) {
      console.error('[Markdown] Error:', err);
      setActionError(err instanceof Error ? err.message : 'Markdown conversion failed');
    } finally {
      setMarkdownConverting(false);
      setMdProgress(null);
      setMdStreamChars(0);
    }
  }, [doc.id, onRefresh]);

  // Clean up on unmount
  useEffect(() => {
    return () => { cancelRef.current = true; };
  }, []);

  const loadRequirements = useCallback(async () => {
    if (currentStep < 3) return;
    setIsLoadingReqs(true);
    try {
      const reqs = await fetchRegulatoryRequirements(doc.id, {
        category: categoryFilter || undefined,
        priority: priorityFilter || undefined,
      });
      setRequirements(reqs);
    } catch {
      // Silently handle - requirements may not exist yet
      setRequirements([]);
    } finally {
      setIsLoadingReqs(false);
    }
  }, [doc.id, currentStep, categoryFilter, priorityFilter]);

  useEffect(() => {
    loadRequirements();
  }, [loadRequirements]);

  const runAction = async (action: string, fn: () => Promise<void>) => {
    setActionLoading(action);
    setActionError(null);
    try {
      await fn();
      onRefresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : `${action} failed`);
    } finally {
      setActionLoading(null);
    }
  };

  const categories = Array.from(new Set(requirements.map((r) => r.category).filter(Boolean))).sort();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button onClick={onBack} className="mt-1 p-1 text-gray-400 hover:text-gray-600">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-900">{doc.name}</h2>
          {doc.description && <p className="mt-1 text-sm text-gray-500">{doc.description}</p>}
          <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
            {doc.source && <span>Source: {doc.source}</span>}
            <span>Uploaded: {new Date(doc.created_at).toLocaleDateString()}</span>
            {doc.total_requirements > 0 && <span>{doc.total_requirements} requirements</span>}
          </div>
        </div>
      </div>

      {/* Processing Steps */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-sm font-medium text-gray-900 mb-4">Processing Pipeline</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {/* Step 1: Upload (always done) */}
          <StepCard
            number={1}
            title="Uploaded"
            description="PDF stored"
            status="completed"
            icon={<FileText className="h-5 w-5" />}
          />

          {/* Step 2: OCR */}
          <StepCard
            number={2}
            title="Process PDF"
            description="OCR text extraction"
            status={currentStep >= 1 ? 'completed' : 'pending'}
            icon={<Play className="h-5 w-5" />}
            actionLabel="Process PDF"
            actionDisabled={currentStep >= 1}
            isLoading={actionLoading === 'ocr'}
            onAction={() => runAction('ocr', () => processRegulatoryDocument(doc.id))}
          />

          {/* Step 3: Markdown */}
          <StepCard
            number={3}
            title="Convert Markdown"
            description={markdownConverting ? markdownProgressLabel : 'Clean formatting'}
            status={currentStep >= 2 ? 'completed' : currentStep >= 1 ? 'pending' : 'locked'}
            icon={<FileText className="h-5 w-5" />}
            actionLabel="Convert"
            actionDisabled={currentStep < 1 || currentStep >= 2 || markdownConverting}
            isLoading={markdownConverting}
            onAction={runMarkdownConversion}
          />

          {/* Step 4: Extract Requirements */}
          <StepCard
            number={4}
            title="Extract Requirements"
            description="AI requirement extraction"
            status={currentStep >= 3 ? 'completed' : currentStep >= 2 ? 'pending' : 'locked'}
            icon={<List className="h-5 w-5" />}
            actionLabel="Extract"
            actionDisabled={currentStep < 2 || currentStep >= 3}
            isLoading={actionLoading === 'extract'}
            onAction={() => runAction('extract', async () => {
              await extractRegulatoryRequirements(doc.id);
              await generateRegulatoryEmbeddings(doc.id);
            })}
          />
        </div>

        {actionError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            {actionError}
          </div>
        )}

        {doc.status === 'processing' && !actionLoading && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-700">
            OCR processing may have stalled or timed out. Click "Process PDF" to retry.
          </div>
        )}

        {doc.status === 'failed' && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            Processing failed. You can retry from the last successful step.
          </div>
        )}
      </div>

      {/* Requirements Table */}
      {currentStep >= 3 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Requirements ({requirements.length})
              </h3>
              <div className="flex gap-3">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">All Categories</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">All Priorities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="standard">Standard</option>
                  <option value="informational">Informational</option>
                </select>
                <button onClick={loadRequirements} className="p-2 text-gray-400 hover:text-gray-600">
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {isLoadingReqs ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
              <span className="ml-2 text-sm text-gray-600">Loading requirements...</span>
            </div>
          ) : requirements.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">
              No requirements found with the current filters.
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {requirements.map((req) => (
                <div key={req.id} className="px-6 py-3 hover:bg-gray-50">
                  <div
                    className="flex items-start gap-3 cursor-pointer"
                    onClick={() => setExpandedReq(expandedReq === req.id ? null : req.id)}
                  >
                    <span className="mt-0.5 text-gray-400">
                      {expandedReq === req.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </span>
                    <span className="text-sm font-mono text-gray-500 w-20 flex-shrink-0">
                      {req.requirement_number}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate">{req.text}</p>
                    </div>
                    <span className="text-xs text-gray-500 flex-shrink-0">{req.category}</span>
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full flex-shrink-0 ${PRIORITY_COLORS[req.priority] || PRIORITY_COLORS.standard}`}>
                      {req.priority}
                    </span>
                    {req.section_reference && (
                      <span className="text-xs text-gray-400 flex-shrink-0">{req.section_reference}</span>
                    )}
                  </div>
                  {expandedReq === req.id && (
                    <div className="mt-2 ml-10 p-3 bg-gray-50 rounded text-sm space-y-2">
                      <p className="text-gray-800 whitespace-pre-wrap">{req.text}</p>
                      {req.cfr_citations.length > 0 && (
                        <p className="text-gray-500">
                          <span className="font-medium">CFR Citations:</span> {req.cfr_citations.join(', ')}
                        </p>
                      )}
                      {req.applicability_notes && (
                        <p className="text-gray-500">
                          <span className="font-medium">Applicability:</span> {req.applicability_notes}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Step card sub-component
interface StepCardProps {
  number: number;
  title: string;
  description: string;
  status: 'completed' | 'pending' | 'locked';
  icon: React.ReactNode;
  actionLabel?: string;
  actionDisabled?: boolean;
  isLoading?: boolean;
  onAction?: () => void;
}

const StepCard: React.FC<StepCardProps> = ({
  number,
  title,
  description,
  status,
  icon,
  actionLabel,
  actionDisabled,
  isLoading,
  onAction,
}) => {
  const borderColor = status === 'completed' ? 'border-green-300 bg-green-50' : status === 'pending' ? 'border-gray-300' : 'border-gray-200 bg-gray-50';
  const iconColor = status === 'completed' ? 'text-green-600' : status === 'pending' ? 'text-gray-600' : 'text-gray-400';

  return (
    <div className={`border rounded-lg p-4 ${borderColor}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={iconColor}>{icon}</span>
        <span className="text-sm font-medium text-gray-900">{title}</span>
      </div>
      <p className="text-xs text-gray-500 mb-3">{description}</p>
      {actionLabel && onAction && (
        <button
          onClick={onAction}
          disabled={actionDisabled || isLoading}
          className="w-full inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1" />
              Processing...
            </>
          ) : status === 'completed' ? (
            'Done'
          ) : (
            actionLabel
          )}
        </button>
      )}
    </div>
  );
};
